import { X } from "lucide-react";
import { useMemo, type Dispatch, type SetStateAction } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { colocaSurfaceVariants } from "@/shared/components/layout/colocaSurfaceVariants";
import { useCountryLabel } from "@/shared/hooks/useCountryLabel";
import useTranslations from "@/shared/lang/useTranslations";

import { SUPPORTED_COUNTRY_OPTIONS } from "../constants/clients.constants";
import type { ClientsFiltersState } from "../types/clients.types";

const ALL_VALUE = "all";

export interface ClientsFiltersBarProps {
  /**
   * Level 1 ("organizations") shows search + country; level 2 ("clients") adds the type filter. Filters the
   * QA backend declares but does not apply yet (type on level 1, KYC status and verified-only on level 2)
   * render disabled with a "coming soon" pill.
   */
  mode: "organizations" | "clients";
  filters: ClientsFiltersState;
  setFilters: Dispatch<SetStateAction<ClientsFiltersState>>;
  appliedFilters: ClientsFiltersState;
  onApply: () => void;
  onReset: () => void;
  onRemoveFilter: (key: keyof ClientsFiltersState) => void;
  isLoading?: boolean;
}

interface ActiveChip {
  key: keyof ClientsFiltersState;
  label: string;
  value: string;
}

export function ClientsFiltersBar({
  mode,
  filters,
  setFilters,
  appliedFilters,
  onApply,
  onReset,
  onRemoveFilter,
  isLoading = false,
}: ClientsFiltersBarProps) {
  const { t } = useTranslations("clients");
  const countryLabel = useCountryLabel();

  const typeOptions = useMemo(
    () => [
      { value: ALL_VALUE, label: t("filters_option_all") },
      { value: "individual", label: t("table_type_individual") },
      { value: "business", label: t("table_type_empresa") },
    ],
    [t],
  );

  const countryOptions = useMemo(
    () => [
      { value: ALL_VALUE, label: t("filters_option_all") },
      ...SUPPORTED_COUNTRY_OPTIONS.map((option) => ({
        value: option.value,
        label: (
          <span className="inline-flex items-center gap-2">
            {option.iso2 && (
              <img
                src={`https://flagcdn.com/${option.iso2.toLowerCase()}.svg`}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="h-3 w-4.5 shrink-0 rounded-[2px] object-cover"
              />
            )}
            {countryLabel(option.value, option.label)}
          </span>
        ),
      })),
    ],
    [t, countryLabel],
  );

  const kycOptions = useMemo(() => [{ value: ALL_VALUE, label: t("filters_option_all") }], [t]);

  const activeChips = useMemo<ActiveChip[]>(() => {
    const chips: ActiveChip[] = [];
    if (appliedFilters.search.trim()) {
      chips.push({ key: "search", label: t("filters_chip_search"), value: appliedFilters.search.trim() });
    }
    if (appliedFilters.client_type) {
      chips.push({
        key: "client_type",
        label: t("filters_chip_type"),
        value:
          appliedFilters.client_type === "business" ? t("table_type_empresa") : t("table_type_individual"),
      });
    }
    if (appliedFilters.country) {
      const country = SUPPORTED_COUNTRY_OPTIONS.find((option) => option.value === appliedFilters.country);
      chips.push({
        key: "country",
        label: t("filters_chip_country"),
        value: countryLabel(appliedFilters.country, country?.label ?? appliedFilters.country),
      });
    }
    return chips;
  }, [appliedFilters, t, countryLabel]);

  const comingSoonPill = (
    <ColocaStatusPill variant="info" size="sm">
      {t("coming_soon")}
    </ColocaStatusPill>
  );

  return (
    <section className={colocaSurfaceVariants()}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="min-w-0 flex-1">
          <ColocaSearchInput
            value={filters.search}
            onValueChange={(value) => setFilters((prev) => ({ ...prev, search: value }))}
            placeholder={
              mode === "organizations"
                ? t("filters_search_organizations_placeholder")
                : t("filters_search_clients_placeholder")
            }
            aria-label={t("filters_label_search")}
            onKeyDown={(event) => {
              if (event.key === "Enter") onApply();
            }}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-end">
          <div className="flex w-full flex-col gap-1 sm:w-40">
            <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              {t("filters_label_client_type")}
              {mode === "organizations" && comingSoonPill}
            </span>
            <ColocaSelect
              value={filters.client_type || ALL_VALUE}
              onValueChange={(value) =>
                setFilters((prev) => ({ ...prev, client_type: value === ALL_VALUE ? "" : value }))
              }
              options={typeOptions}
              disabled={mode === "organizations"}
              aria-label={t("filters_label_client_type")}
            />
          </div>

          {mode === "clients" && (
            <div className="flex w-full flex-col gap-1 sm:w-44">
              <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                {t("filters_label_kyc_status")}
                {comingSoonPill}
              </span>
              <ColocaSelect
                value={ALL_VALUE}
                onValueChange={() => {}}
                options={kycOptions}
                disabled
                aria-label={t("filters_label_kyc_status")}
              />
            </div>
          )}

          <div className="flex w-full flex-col gap-1 sm:w-44">
            <span className="text-xs font-medium text-muted-foreground">{t("filters_label_country")}</span>
            <ColocaSelect
              value={filters.country || ALL_VALUE}
              onValueChange={(value) =>
                setFilters((prev) => ({ ...prev, country: value === ALL_VALUE ? "" : value }))
              }
              options={countryOptions}
              aria-label={t("filters_label_country")}
            />
          </div>

          {mode === "clients" && (
            <div className="flex h-9 items-center gap-2 self-end">
              <ColocaSwitch
                checked={false}
                onCheckedChange={() => {}}
                disabled
                size="sm"
                aria-label={t("filters_verified_only")}
              />
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {t("filters_verified_only")}
                {comingSoonPill}
              </span>
            </div>
          )}

          <div className="col-span-2 flex items-center gap-2 self-end">
            <ColocaButton variant="primary" onClick={onApply} disabled={isLoading}>
              {t("filters_button_apply")}
            </ColocaButton>
            <ColocaButton variant="ghost" onClick={onReset} disabled={isLoading}>
              {t("filters_button_clear")}
            </ColocaButton>
          </div>
        </div>
      </div>

      {activeChips.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
          {activeChips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={() => onRemoveFilter(chip.key)}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 py-1 pl-3 pr-2 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={t("filters_chip_remove", { label: chip.label, value: chip.value })}
            >
              {chip.label}: <span className="font-medium text-foreground">{chip.value}</span>
              <X className="size-3" aria-hidden="true" />
            </button>
          ))}
          <ColocaButton variant="ghost" size="xs" onClick={onReset}>
            {t("filters_clear_all")}
          </ColocaButton>
        </div>
      )}
    </section>
  );
}
